import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Loading spinner component with ARIA attributes for accessibility
 * 
 * This component provides a standardized loading indicator throughout the application
 * with proper accessibility support for screen readers.
 */
interface LoadingProps {
  /** Text to display for screen readers and as visible label if showText is true */
  text?: string;
  /** Whether to show the loading text visibly */
  showText?: boolean;
  /** Size of the loading spinner */
  size?: 's' | 'm' | 'l';
  /** Additional CSS class */
  className?: string;
}

const Loading: React.FC<LoadingProps> = ({
  text = 'Loading...',
  showText = true,
  size = 'm',
  className = '',
}) => {
  const containerClasses = `loading-container ${className}`;
  const spinnerSize = size === 's' ? '1.5rem' : size === 'l' ? '3rem' : '2rem';
  
  return (
    <div 
      className={containerClasses}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="relative flex items-center justify-center">
        {/* Outer glowing ring */}
        <div 
          className="absolute rounded-full border border-[var(--brand-primary)]/30 animate-ping"
          style={{ width: `calc(${spinnerSize} + 1rem)`, height: `calc(${spinnerSize} + 1rem)` }}
        />
        
        {/* Core spinner */}
        <Loader2 
          className="animate-spin text-[var(--brand-primary)] drop-shadow-[0_0_8px_rgba(61,122,116,0.6)]" 
          size={spinnerSize} 
          strokeWidth={2.5}
        />
      </div>
      
      {showText && (
        <span className="mt-4 text-sm font-medium text-[var(--text-secondary)] animate-pulse">
          {text}
        </span>
      )}
      {!showText && <span className="sr-only">{text}</span>}
      
      <style dangerouslySetInnerHTML={{ __html: `
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 1.5rem;
          width: 100%;
          min-height: 100px;
        }
        
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border-width: 0;
        }
      `}} />
    </div>
  );
};

export default Loading; 